import React, { useState } from 'react'
import { X, FileText, Upload } from 'lucide-react'
import { apiService } from '../../services/api'
import { toast } from 'react-toastify'

interface RfqResponseModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  rfqId: string
  rfqTitle: string
  buyerCompany: string
  rfqType?: 'enterprise' | 'government' // Add RFQ type
}

interface FormData {
  responseText: string
  proposedPrice: string
  deliveryDays: string
}

export const RfqResponseModal: React.FC<RfqResponseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  rfqId,
  rfqTitle,
  buyerCompany,
  rfqType = 'enterprise' // Default to enterprise
}) => {
  const [formData, setFormData] = useState<FormData>({
    responseText: '',
    proposedPrice: '',
    deliveryDays: ''
  })
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  if (!isOpen) return null

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])
    setFiles(prev => [...prev, ...selectedFiles])
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.responseText.trim()) {
      newErrors.responseText = 'Response text is required'
    } else if (formData.responseText.length < 20) {
      newErrors.responseText = 'Response must be at least 20 characters'
    }

    if (formData.proposedPrice && isNaN(Number(formData.proposedPrice))) {
      newErrors.proposedPrice = 'Proposed price must be a valid number'
    }

    if (formData.deliveryDays && isNaN(Number(formData.deliveryDays))) {
      newErrors.deliveryDays = 'Delivery days must be a valid number'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('responseText', formData.responseText)
      if (formData.proposedPrice) {
        formDataToSend.append('proposedPrice', formData.proposedPrice)
      }
      if (formData.deliveryDays) {
        formDataToSend.append('deliveryDays', formData.deliveryDays)
      }

      // Add files
      files.forEach(file => {
        formDataToSend.append('attachments', file)
      })

      // Use the correct endpoint based on RFQ type
      const endpoint = rfqType === 'government' 
        ? `/api/gov/rfqs/${rfqId}/respond`
        : `/api/enterprise/rfqs/${rfqId}/respond`

      await apiService.post(endpoint, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      toast.success('Response submitted successfully!')
      onSuccess()
      handleClose()
    } catch (error: any) {
      console.error('Error submitting response:', error)
      toast.error(error.response?.data?.message || 'Failed to submit response')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      responseText: '',
      proposedPrice: '',
      deliveryDays: ''
    })
    setFiles([])
    setErrors({})
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Submit Response</h2>
            <p className="text-sm text-gray-500 mt-1">
              Responding to: {rfqTitle} by {buyerCompany}
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Response Text */}
          <div>
            <label htmlFor="responseText" className="block text-sm font-medium text-gray-700 mb-2">
              Your Response *
            </label>
            <textarea
              id="responseText"
              value={formData.responseText}
              onChange={(e) => handleInputChange('responseText', e.target.value)}
              rows={6}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.responseText ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Describe your approach, capabilities, and how you can meet the requirements..."
            />
            {errors.responseText && <p className="mt-1 text-sm text-red-600">{errors.responseText}</p>}
          </div>

          {/* Proposed Price */}
          <div>
            <label htmlFor="proposedPrice" className="block text-sm font-medium text-gray-700 mb-2">
              Proposed Price ($)
            </label>
            <input
              type="number"
              id="proposedPrice"
              value={formData.proposedPrice}
              onChange={(e) => handleInputChange('proposedPrice', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.proposedPrice ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your proposed price"
              min="0"
              step="0.01"
            />
            {errors.proposedPrice && <p className="mt-1 text-sm text-red-600">{errors.proposedPrice}</p>}
          </div>

          {/* Delivery Days */}
          <div>
            <label htmlFor="deliveryDays" className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Days
            </label>
            <input
              type="number"
              id="deliveryDays"
              value={formData.deliveryDays}
              onChange={(e) => handleInputChange('deliveryDays', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.deliveryDays ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Number of days to deliver"
              min="1"
            />
            {errors.deliveryDays && <p className="mt-1 text-sm text-red-600">{errors.deliveryDays}</p>}
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supporting Documents
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-primary-600 hover:text-primary-700 font-medium">
                    Upload files
                  </span>
                  <span className="text-gray-500"> or drag and drop</span>
                </label>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                PDF, DOC, DOCX, XLS, XLSX up to 10MB each
              </p>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Response'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
} 